// TripticLanding — the new Triptic.ai marketing landing page | student-focused single CTA | manages shared modal state (AuthModal) and auth awareness

import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";

const LandingCollegeExplorer = lazy(() => import('../components/landing/LandingCollegeExplorer'));
import TripticNav from "../components/landing/TripticNav";
import TriptychHero from "../components/landing/TriptychHero";
import HowItWorks from "../components/landing/HowItWorks";
import ProblemSection from "../components/landing/ProblemSection";
import ChainReaction from "../components/landing/ChainReaction";
import PortalShowcase from "../components/landing/PortalShowcase";
import type { PortalKey } from "../components/landing/PortalShowcase";
import FinalCTA from "../components/landing/FinalCTA";
import Footer from "../components/landing/Footer";
import RoleSelector from "../components/landing/RoleSelector";
import AuthModal from "../components/marketplace/AuthModal";
import AnimatedBackground from "../components/landing/AnimatedBackground";
import { UserAuth } from "../context/AuthContext";

function TripticLanding() {
  const navigate = useNavigate();
  const { session } = UserAuth();
  const isLoggedIn = !!session;

  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signup');
  const [authRole, setAuthRole] = useState<'student' | 'counselor' | undefined>(undefined);
  const [showRoleSelector, setShowRoleSelector] = useState(false);

  // Which portal card the nav asked for — highlighted briefly, then cleared
  const [highlightedPortal, setHighlightedPortal] = useState<PortalKey | null>(null);

  // Scroll-snap for the ProblemSection moments.
  // scroll-snap-type only applies to the element that is ACTUALLY the scroll
  // container. `overflow-x: hidden` on html (index.css) stops the viewport
  // scrolling box from propagating to html, so BODY is the real scroller —
  // hence the class goes on document.body, not documentElement.
  // Cleanup is defense-in-depth: a stuck snap on body breaks scrolling app-wide.
  useEffect(() => {
    const CLASS = 'landing-snap'
    const body = document.body
    const root = document.documentElement

    // Strip any stale class first (hard refresh mid-scroll, prior crashed unmount).
    // Also clears the old html-targeted class from a previous build.
    body.classList.remove(CLASS)
    root.classList.remove(CLASS)
    body.classList.add(CLASS)

    // classList.remove is idempotent — safe to call twice or when absent
    const cleanup = () => {
      body.classList.remove(CLASS)
      root.classList.remove(CLASS)
    }

    // pagehide covers bfcache restore, where unmount may not fire
    window.addEventListener('pagehide', cleanup)

    return () => {
      cleanup()
      window.removeEventListener('pagehide', cleanup)
    }
  }, [])

  // Auto-open modal if AuthCallback stored an error after OAuth redirect
  useEffect(() => {
    try {
      if (sessionStorage.getItem('auth_error')) {
        setAuthMode('signin')
        setAuthRole(undefined)
        setShowAuth(true)
      }
    } catch { /* ignore */ }
  }, [])

  // "/" resolves the right portal from the user's actual profile row, so every
  // signed-in path goes through it rather than guessing from user_metadata.
  const goToPortal = () => navigate('/');

  const handleSignIn = () => {
    if (session) {
      goToPortal()
    } else {
      setAuthMode('signin')
      setAuthRole(undefined)
      setShowAuth(true)
    }
  };

  const handleDashboard = () => goToPortal();

  const enterAs = (role: 'student' | 'counselor') => {
    if (session) {
      goToPortal()
      return
    }
    setAuthMode('signup')
    setAuthRole(role)
    setShowAuth(true)
  };

  const handleEnterStudent = () => enterAs('student');
  const handleEnterCounselor = () => enterAs('counselor');

  // Nav tab clicked — scroll to the whole section, then flag which card was asked for
  const handleNavPortal = (portal: PortalKey) => setHighlightedPortal(portal);

  return (
    <div className="triptic-landing min-h-screen">
      <AnimatedBackground />
      <div className="relative z-10">
        <TripticNav
          onSignIn={handleSignIn}
          onDashboard={handleDashboard}
          isLoggedIn={isLoggedIn}
          onPortalSelect={handleNavPortal}
        />
        <TriptychHero
          onEnterStudent={handleEnterStudent}
          onEnterCounselor={handleEnterCounselor}
          onTryItNow={() => {}}
        />

        {/* Ticker now lives inside TriptychHero, pinned to the hero's bottom
            edge. It previously sat here at zIndex 20 and painted over the
            "How it works" heading (zIndex 1). */}

        {/* Hero leads straight into the 4 problem moments — no beat between */}
        <ProblemSection />

        {/* The full narrative in one beat: problem lines accumulate, then the
            payoff. scrollPastSection lands here. */}
        <ChainReaction />

        <HowItWorks />

        <PortalShowcase
          highlightedPortal={highlightedPortal}
          onHighlightEnd={() => setHighlightedPortal(null)}
        />

        {/* College explorer */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              textAlign: 'center',
              padding: '0 24px',
              marginBottom: 32,
              marginTop: 72,
            }}
          >
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.3)',
                margin: 0,
              }}
            >
              INSIDE THE WORKSPACE
            </p>
            <h2
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(32px, 5vw, 52px)',
                fontWeight: 700,
                color: '#FFFFFF',
                lineHeight: 1.1,
                margin: '16px 0 0',
              }}
            >
              Find your fit.
            </h2>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 15,
                fontWeight: 300,
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.7,
                maxWidth: 560,
                margin: '16px auto 0',
              }}
            >
              Every student gets a live college explorer — search by location, major,
              test scores, and cost. This is one piece of the workspace, free to try right now.
            </p>
          </div>
          <Suspense fallback={null}>
            <LandingCollegeExplorer />
          </Suspense>
        </div>

        {/* Final CTA */}
        <FinalCTA />

        <Footer />

        <RoleSelector isOpen={showRoleSelector} onClose={() => setShowRoleSelector(false)} />

        {/* Shared auth modal — defaultRole from main so enterAs() can preselect */}
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} defaultMode={authMode} defaultRole={authRole} />
      </div>
    </div>
  );
}

export default TripticLanding;
